import { FC, useCallback, useState } from "react";
import FamilyMemberList from "@components/Onboarding/OnboardingFamilyInfoForm/FamilyMemberList";
import { useTranslation } from "react-i18next";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import ValidationError from "@components/ValidationError/ValidationError";
import {
  FamilyMemberFormType,
  PatientFormType,
} from "@utils/patientFormHelper";
import styled from "@emotion/styled";
import { Box, Stack, Typography } from "@mui/material";
import TextIconButton from "@components/TextIconButton";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { Character } from "@models/character";
import StyledLink from "@components/StyledLink/StyledLink";
import EmptyState from '@components/EmptyState/EmptyState';

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FamilyInfoForm: FC<{
  form: UseFormReturn<PatientFormType>;
  onEdit: (familyMember: FamilyMemberFormType) => void;
  onAdd: () => void;
  onSave: () => void;
  characters: Character[];
}> = ({ onEdit, onAdd, form, onSave, characters }) => {
  const { t } = useTranslation();
  const [selectedFamilyMembers, setSelectedFamilyMembers] = useState<number[]>(
    [],
  );

  const {
    control,
    formState: { errors },
  } = form;

  const { fields, remove, update } = useFieldArray<
    PatientFormType,
    "familyMembers"
  >({
    control,
    name: "familyMembers",
    keyName: "_id" as any,
  });

  const onRemove = useCallback(
    (index: number) => {
      const familyMember = fields[index];
      if (!familyMember) {
        return;
      }
      if (!familyMember.id) {
        remove(index);
      } else {
        update(index, { ...familyMember, delete: true });
      }
    },
    [remove, fields, update],
  );

  const onSelect = useCallback((familyMemberIndex: number) => {
    setSelectedFamilyMembers((familyMembers) => {
      const index = familyMembers.indexOf(familyMemberIndex);
      if (index > -1) {
        return [
          ...familyMembers.slice(0, index),
          ...familyMembers.slice(index + 1),
        ];
      }
      return [...familyMembers, familyMemberIndex];
    });
  }, []);

  const onRemoveSelected = useCallback(() => {
    if (!selectedFamilyMembers.length) {
      return;
    }

    selectedFamilyMembers.forEach((index) => {
      onRemove(index);
    });

    onSave();

    setSelectedFamilyMembers([]);
  }, [selectedFamilyMembers, onRemove, onSave]);

  return (
    <FormContainer>
      <Box display="flex" justifyContent="space-between">
        <TextIconButton
          disabled={!selectedFamilyMembers.length}
          variant="outlined"
          text={t("familyMembers.deleteButton")}
          color="error"
          iconLeft={true}
          icon={DeleteOutlineOutlinedIcon}
          onClick={onRemoveSelected}
        />

        <TextIconButton
          variant="outlined"
          text={t("familyMembers.newButton")}
          color="primary"
          iconLeft={true}
          icon={AddOutlinedIcon}
          onClick={onAdd}
        />
      </Box>

      {fields.length ? (
        <FamilyMemberList
          selectedFamilyMembers={selectedFamilyMembers}
          familyMembers={fields}
          onEdit={onEdit}
          onRemove={onRemove}
          onSelect={onSelect}
          characters={characters}
        />
      ) : (
        <>
          <EmptyState image={`${process.env.PUBLIC_URL}/images/art-family.jpeg`}>
            <Stack
              flexDirection="row"
              gap="8px"
              fontSize="14px"
              lineHeight="22px"
              fontWeight="400"
              marginTop="24px"
              justifyContent="center"
            >
              <Typography
                color="#171A1FFF"
                fontSize="14px"
                lineHeight="22px"
                fontWeight="400"
              >
                {t("familyMembers.emptyListLabel")}
              </Typography>
              <Box onClick={onAdd}>
                <StyledLink to="">{t("familyMembers.addButton")}</StyledLink>
              </Box>
            </Stack>
          </EmptyState>
        </>
      )}

      {errors?.root && (
        <ValidationError>{t(`errors.${errors.root.type}`)}</ValidationError>
      )}
    </FormContainer>
  );
};

export default FamilyInfoForm;
